/**
 * What colour a row is, and where that colour is shown.
 *
 * Two questions, deliberately kept apart, because the backend keeps them apart:
 * a *decorator* says where the colour goes -- a left border or the whole row --
 * and a *value provider* says which colour a given row gets. A decoration
 * stores one of each, and either provider drives either decorator.
 *
 * Pure, so the answer for a row is a function of the row and the decoration and
 * nothing else. That matters more here than it looks: the conditional provider
 * decides by running the *same filters* a view uses to choose its rows, so this
 * calls `rowMatchesFilters` rather than reimplementing a second, quietly
 * different idea of what "matches" means.
 *
 * The configurations are read through a cast. A decoration arriving *on a view*
 * is a `ViewDecoration`, whose `value_provider_conf` is `unknown` -- one field
 * holding two entirely different shapes, which the view's read serializer does
 * not describe. The decoration endpoints do describe them, so the shapes
 * themselves are the generated ones and only which of the two applies is
 * decided here, from the provider type beside it.
 */
import type {
  ConditionalColorConf,
  Field,
  RowData,
  SelectColorConf,
  ViewDecoration,
} from '@/api/types'
import { fieldKey } from '@/api/types'
import type { FilterContext } from '../filters'
import { rowMatchesFilters } from '../rows/rowMatch'

/** A single select cell, which is where `single_select_color` reads from. */
interface SelectCell {
  color?: string | null
}

/**
 * The colour one decoration gives a row, or null for none.
 *
 * Null in three different circumstances that all mean the same thing to a
 * reader -- the column was deleted, the cell is empty, or no condition matched
 * -- so they are one answer rather than three.
 */
export function decorationColor(
  decoration: ViewDecoration,
  row: RowData,
  fields: readonly Field[],
  context?: FilterContext,
): string | null {
  if (decoration.value_provider_type === 'single_select_color') {
    const conf = (decoration.value_provider_conf ?? {}) as SelectColorConf
    const fieldId = conf.field_id
    // One expression, not a guard and then a fallback. "The decoration names no
    // column" and "the cell is empty" are the same answer, and writing the
    // first as an early return made a branch no test could tell from its
    // absence -- reading `field_null` finds nothing either.
    const cell =
      fieldId === null || fieldId === undefined
        ? undefined
        : (row[fieldKey(fieldId)] as SelectCell | null | undefined)
    return cell?.color ?? null
  }

  if (decoration.value_provider_type === 'conditional_color') {
    const conf = (decoration.value_provider_conf ?? {}) as ConditionalColorConf
    for (const colour of conf.colors ?? []) {
      // The filter machinery takes a view-shaped thing, and a colour is one:
      // its own connective, its own conditions, its own groups.
      const matches = rowMatchesFilters(
        row,
        {
          filter_type: colour.operator ?? 'AND',
          filters: colour.filters,
          filter_groups: colour.filter_groups ?? [],
        },
        fields,
        context,
      )
      // The first match wins, which is what makes the order of the list the
      // order the conditions are tried in.
      if (matches) return colour.color
    }
    return null
  }

  // A provider this build does not know: a view configured by another version,
  // or one whose type has since been unregistered. Drawn plain rather than
  // guessed at.
  return null
}

/** What a row looks like once every decoration has had its say. */
export interface RowColors {
  leftBorder: string | null
  background: string | null
}

/**
 * The colours to draw a row in.
 *
 * Decorations are applied in `order`, and the last one to answer for a given
 * place wins -- which matches the backend's own reading of `order` ("lowest
 * first") and means the decoration nearest the bottom of the list is the one on
 * top, the way a stack of rules usually reads.
 */
export function rowColors(
  decorations: readonly ViewDecoration[],
  row: RowData,
  fields: readonly Field[],
  context?: FilterContext,
): RowColors {
  const colors: RowColors = { leftBorder: null, background: null }

  for (const decoration of [...decorations].sort(byOrder)) {
    const color = decorationColor(decoration, row, fields, context)
    if (color === null) continue
    if (decoration.type === 'left_border_color') colors.leftBorder = color
    else if (decoration.type === 'background_color') colors.background = color
  }

  return colors
}

/** Lowest order first, and the lowest id first within an order, as documented. */
const byOrder = (a: ViewDecoration, b: ViewDecoration): number =>
  (a.order ?? 0) - (b.order ?? 0) || a.id - b.id
